import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Input, Select, DatePicker, Space, Button, message, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title } = Typography;

export default function AuditLogs() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({
    module: undefined,
    action: undefined,
    userId: undefined,
    dateRange: null,
  });

  const pageSize = 50;

  const fetchLogs = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, pageSize };
      if (filters.module) params.module = filters.module;
      if (filters.action) params.action = filters.action;
      if (filters.userId) params.userId = filters.userId;
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].toISOString();
        params.endDate = filters.dateRange[1].toISOString();
      }
      const res = await api.get('/audit-logs', { params });
      setData(res.data);
      setTotal(res.total);
    } catch (e) {
      message.error('加载审计日志失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await api.get('/audit-logs/modules');
      setModules(res);
    } catch {}
  };

  useEffect(() => { fetchModules(); fetchLogs(1); }, []);

  const handleModuleChange = async (module) => {
    setFilters(prev => ({ ...prev, module, action: undefined }));
    if (module) {
      try {
        const res = await api.get(`/audit-logs/actions/${module}`);
        setActions(res);
      } catch {
        setActions([]);
      }
    } else {
      setActions([]);
    }
  };

  const actionColorMap = {
    login: 'green',
    login_failed: 'red',
    logout: 'default',
    create: 'blue',
    update: 'orange',
    delete: 'red',
    submit: 'purple',
    approve: 'green',
    reject: 'red',
    export: 'cyan',
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (v) => dayjs(v).format('MM-DD HH:mm:ss'),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: '操作人',
      width: 120,
      render: (_, r) => r.user ? `${r.user.name} (${r.user.role})` : r.userId === 'system' ? '系统' : r.userId,
    },
    {
      title: '模块',
      dataIndex: 'module',
      width: 100,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 110,
      render: (v) => <Tag color={actionColorMap[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '目标 ID',
      dataIndex: 'targetId',
      width: 130,
      ellipsis: true,
    },
    {
      title: '详情',
      dataIndex: 'detail',
      ellipsis: true,
      render: (v) => {
        if (!v) return '-';
        try {
          const parsed = typeof v === 'string' ? JSON.parse(v) : v;
          return JSON.stringify(parsed);
        } catch {
          return v;
        }
      },
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      width: 130,
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>审计日志</Title>
          <Button icon={<ReloadOutlined />} onClick={() => fetchLogs(1)}>刷新</Button>
        </div>

        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            placeholder="选择模块"
            allowClear
            style={{ width: 160 }}
            value={filters.module}
            onChange={handleModuleChange}
            options={modules.map(m => ({ label: m, value: m }))}
          />
          <Select
            placeholder="选择操作"
            allowClear
            style={{ width: 140 }}
            value={filters.action}
            onChange={(v) => setFilters(prev => ({ ...prev, action: v }))}
            options={actions.map(a => ({ label: a, value: a }))}
          />
          <Input
            placeholder="用户 ID"
            style={{ width: 140 }}
            value={filters.userId}
            onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
          />
          <RangePicker
            showTime
            onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => { setPage(1); fetchLogs(1); }}>
            查询
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p) => { setPage(p); fetchLogs(p); },
          }}
          scroll={{ x: 900 }}
          size="small"
        />
      </Card>
    </div>
  );
}